import React, { useCallback } from 'react';
import styled from 'styled-components';
import { Flex } from '@cko/primitives';
import InfiniteScrollComponent from 'react-infinite-scroll-component';

import { StyledLoading } from '../StyledHelperComponents';
import { AccountSelectorListItem } from '../AccountSelectorListItem';

export interface ListItem {
  title: string;
  nestedCount?: number;
  isInactive?: boolean;
  isDisabled?: boolean;
}

export interface InfiniteScroll {
  hasMore?: boolean;
  isFetchingMore?: boolean;
  fetchMore?: () => void;
}

interface AccountSelectorListProps<T> {
  listName: string;
  items: Array<T>;
  itemToListItemMapper: (item: T) => ListItem;
  onToggleItem: (item: T) => void;
  selectedItem?: T;
  existingSelectedItem?: T;
  infiniteScroll?: InfiniteScroll;
  disabledMessage?: string;
  isParent: boolean;
}

const ListContainer = styled(Flex)`
  flex-direction: column;
  width: 338px;
  height: 430px;
  justify-content: flex-start;
  padding: var(--layout-soybean) var(--layout-stamp);
`;

const ListNameLabel = styled(Flex)`
  padding: var(--layout-soybean) var(--layout-stamp);
  align-items: center;
  gap: var(--layout-soybean);
  align-self: stretch;
  color: var(--base-text-primary);
  font: var(--heading-sm);
`;

const ListItems = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`;

const LoadingContainer = styled(Flex)`
  padding: var(--layout-sandgrain) var(--layout-shoe);
  width: 100%;
`;

export const AccountSelectorList = <T extends { id: string }>({
  listName,
  items,
  selectedItem,
  existingSelectedItem,
  itemToListItemMapper,
  onToggleItem,
  infiniteScroll,
  disabledMessage,
  isParent,
}: AccountSelectorListProps<T>) => {
  const getMore = useCallback(() => {
    if (infiniteScroll?.hasMore && !infiniteScroll?.isFetchingMore && infiniteScroll?.fetchMore) {
      infiniteScroll?.fetchMore();
    }
  }, [infiniteScroll]);

  /* 
    We manually add the selected item to the top of the list when in infiniteScroll mode and we have an existing item that isn't in the list of clients returned.
    
    When the item comes into view in the regular list, we remove it from the top of the list.
    isnt in the list of clients returned, when the item comes into view in the regular list we stop it from being shown 
    on top  */

  const existingItemNotInList =
    items.find((item) => item.id === existingSelectedItem?.id) === undefined;
  const showExisting =
    infiniteScroll && existingSelectedItem !== undefined && existingItemNotInList;
  const list = showExisting && existingSelectedItem ? [existingSelectedItem, ...items] : items;

  return (
    <ListContainer data-testid={`ac-list-${listName}`}>
      <ListNameLabel data-testid={`ac-list-header-${listName}`}>{listName}</ListNameLabel>
      <ListItems id="scrollableContainer">
        <InfiniteScrollComponent
          scrollableTarget="scrollableContainer"
          dataLength={list.length}
          next={getMore}
          hasMore={!!infiniteScroll?.hasMore}
          loader={
            <LoadingContainer data-testid="infinite-scroll-loader">
              <StyledLoading testId="infinite-scroll-is-loading" />
            </LoadingContainer>
          }
        >
          {list.map((item: T) => {
            const itemForList = itemToListItemMapper(item);
            const isSelected = item.id === selectedItem?.id;
            return (
              <AccountSelectorListItem
                key={item.id}
                {...itemForList}
                isParent={isParent}
                isSelected={isSelected}
                onClick={() => onToggleItem(item)}
                disabledMessage={disabledMessage}
              />
            );
          })}
        </InfiniteScrollComponent>
      </ListItems>
    </ListContainer>
  );
};
